/**
 * Conversation Audit Console (iter135.16) — Level-2 admin UI for the
 * ``ActionLog`` slice where ``entity='conversation'``. Gives Super
 * Admins a searchable view of every conv mutation — CONV_INITIATE,
 * CONV_ADD_COMPANY, CONV_SEND_MESSAGE, CONV_APPLY_UP_SUBMIT,
 * CONV_APPLY_UP_RESPOND, CONV_EDIT_MESSAGE, CONV_RECALL_MESSAGE,
 * CONV_INVITE — without dropping into MySQL or curl.
 *
 * Filters:
 *   • action_type (exact) — dropdown of known CONV_* action types.
 *   • actor (substring on ``actor_email``).
 *   • entity_id (exact match on ``conversation_id``).
 *
 * Read-only. All mutations stay on ``/api/conv/*``.
 */
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { ClipboardText, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";

const KNOWN_ACTIONS = [
  "CONV_INITIATE",
  "CONV_ADD_COMPANY",
  "CONV_SEND_MESSAGE",
  "CONV_EDIT_MESSAGE",
  "CONV_RECALL_MESSAGE",
  "CONV_APPLY_UP_SUBMIT",
  "CONV_APPLY_UP_RESPOND",
  "CONV_INVITE",
];

function formatTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export default function ConvAuditPage({ user, onLogout, onLogoutAll }) {
  const [actionType, setActionType] = useState("");
  const [actor, setActor] = useState("");
  const [entityId, setEntityId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionType) params.set("action_type", actionType);
      if (actor) params.set("actor", actor);
      if (entityId) params.set("entity_id", entityId);
      params.set("limit", "300");
      const r = await api.get(`/admin/conv-ops/audit-log?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [actionType, actor, entityId]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const rowsByAction = useMemo(() => {
    const m = {};
    for (const r of items) m[r.action_type] = (m[r.action_type] || 0) + 1;
    return m;
  }, [items]);

  return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }}>
      <AdminPageHeader
        title="Conversation Audit Console"
        subtitle="Read-only view of every conv mutation in the ActionLog"
        icon={<ClipboardText size={20} weight="duotone" className="text-slate-700" />}
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6 space-y-4">
        {/* Filters */}
        <div
          className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 flex flex-wrap items-end gap-3"
          data-testid="conv-audit-filters"
        >
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Action type</label>
            <select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              data-testid="conv-audit-action-filter"
              className="text-[12px] border border-slate-300 rounded-md px-2 py-1 min-w-[200px]"
            >
              <option value="">All</option>
              {KNOWN_ACTIONS.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Actor email</label>
            <input
              type="text"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              placeholder="substring match"
              data-testid="conv-audit-actor-filter"
              className="text-[12px] border border-slate-300 rounded-md px-2 py-1 w-56"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Conversation ID</label>
            <input
              type="text"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="exact conv_id"
              data-testid="conv-audit-conv-filter"
              className="text-[12px] border border-slate-300 rounded-md px-2 py-1 w-64 font-mono"
            />
          </div>
          <button
            onClick={fetchAudit}
            disabled={loading}
            data-testid="conv-audit-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-700 disabled:opacity-60"
          >
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            Search
          </button>
          <div className="ml-auto text-[11px] text-slate-500">
            {items.length} row(s)
            {Object.keys(rowsByAction).length > 0 && (
              <span className="ml-2">
                ({Object.entries(rowsByAction).map(([k, v]) => `${k}: ${v}`).join(" · ")})
              </span>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="conv-audit-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Timestamp</th>
                  <th className="px-3 py-2 text-left">Actor</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-left">Conversation</th>
                  <th className="px-3 py-2 text-left">Label</th>
                  <th className="px-3 py-2 text-left">Result</th>
                  <th className="px-3 py-2 text-left">IP</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.length === 0 && !loading && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">No rows match the current filters.</td></tr>
                )}
                {items.map((r) => (
                  <Fragment key={r.id}>
                    <tr
                      className={`hover:bg-slate-50 ${r.result !== "success" ? "bg-rose-50" : ""}`}
                      data-testid={`conv-audit-row-${r.id}`}
                    >
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatTs(r.ts_utc)}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{r.actor_email || "—"}</td>
                      <td className="px-3 py-2 text-slate-500">{r.actor_role || "—"}</td>
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700">
                          {r.action_type || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.entity_id || "—"}</td>
                      <td className="px-3 py-2 text-slate-700">{r.entity_label || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          r.result === "success" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                        }`}>
                          {r.result || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.ip_address || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {r.details && (
                          <button
                            onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                            className="text-[11px] text-blue-600 hover:underline"
                            data-testid={`conv-audit-expand-${r.id}`}
                          >
                            {expandedId === r.id ? "hide" : "details"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === r.id && (
                      <tr className="bg-slate-50">
                        <td colSpan={9} className="px-3 py-3">
                          <pre className="text-[10px] bg-white border border-slate-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap">
{r.details ? (() => { try { return JSON.stringify(JSON.parse(r.details), null, 2); } catch { return r.details; } })() : "—"}
                          </pre>
                          {r.error_message && (
                            <div className="mt-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-md p-2">
                              <span className="font-semibold">Error:</span> {r.error_message}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
